import { Divider, Box, Flex, Text, Card, ListItem, List } from '@opendoor/bricks/core';
import { EventCard } from '@opendoor/cinderblocks/platform/EventCard';
import { generatePath } from 'react-router-dom';

import { Layout } from 'platform/components/layout/Layout';
import {
  TasksSectionContent,
  TasksSectionContentProps,
} from 'shared/components/TasksSectionContent';
import { SellerIndependentPageHeader } from 'sellDirect/components/offer/SellerHeader';
import { useSSVASubtasks } from 'sellDirect/helpers/useSSVASubtasks';
import React, { useEffect } from 'react';
import { TextReminderTrigger } from 'sellDirect/components/ssva/TextReminderTrigger';
import { SSVACommonQuestions } from 'sellDirect/components/ssva/SSVACommonQuestions';
import { useViewedSSVA } from 'sellDirect/graphql/useViewedSSVA';
import { PreClosingOffer, useOfferContext } from 'sellDirect/OfferContext';
import { getHubOverviewRoute, getHubVirtualAssessmentQuestionsRoute } from 'shared/helpers/routes';
import { Redirect } from 'platform/react-router';
import { VALayout } from 'sellDirect/components/va/VALayout';
import { useParticipantsModal } from 'sellDirect/helpers/ParticipantsProvider';
import { useVirtualAssessmentData } from 'sellDirect/graphql/useVirtualAssessmentData';
import { OdProtosWebData_ORVAState as ORVAState } from '__generated__/globalTypes';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

/** SSVA page that shows the current state of SSVA progress. */
export const SSVATasksPage: React.FC<{ offer: PreClosingOffer }> = ({ offer }) => {
  const { openParticipantsModal } = useParticipantsModal();
  const { loading, tasks } = useSSVASubtasks();
  const { updateViewedSSVA } = useViewedSSVA();
  const { addressUUID } = useQualificationContext();
  const {
    ssvaData: { hasViewedSSVAPage, ssvaState },
    orvaData: { orvaState },
    isExpired,
  } = offer;
  const {
    shouldSeeAgentExperience,
    experimentationData: { shouldSeeVAQuestionPage, shouldSeeSsvaNooExperience },
  } = useOfferContext();
  const { refetch: refetchVirtualAssessmentData } = useVirtualAssessmentData();
  const completedTasks = tasks?.filter((task) => task.isDone);
  const remainingTasks = tasks?.filter((task) => !task.isDone);

  /** Triggers a mutation when this is a user's first time on the ssva xp page. Double checks that they haven't started any type of upload yet */
  useEffect(() => {
    if (!loading && !hasViewedSSVAPage && ssvaState === 'not_started') {
      updateViewedSSVA();
    }
    //eslint-disable-next-line
  }, [loading, ssvaState, hasViewedSSVAPage]);

  useEffect(() => {
    /**
     * Refetch VA data if user switched tabs then switched back.
     * This is for making sure the page has the up-to-date SSVA status.
     */
    const visibilityListener = () => {
      document.visibilityState === 'visible' && refetchVirtualAssessmentData();
    };

    document.addEventListener('visibilitychange', visibilityListener);
    return () => {
      document.removeEventListener('visibilitychange', visibilityListener);
    };
  }, [refetchVirtualAssessmentData, shouldSeeAgentExperience]);

  if (isExpired) {
    /*
    Redirect to the overview page if this user is loading on /upload
    but it's not sellDirect qualified, or the offer has already expired
    , or the customer is an agent customer
    */
    return <Redirect to={generatePath(getHubOverviewRoute(), { addressUUID })} />;
  }

  if (shouldSeeVAQuestionPage) {
    /**
     * Redirect to the question page if the user is in treatment,
     * has not started a VA
     * and has not answered the Occupancy question
     */
    return (
      <Redirect
        to={generatePath(getHubVirtualAssessmentQuestionsRoute(), {
          addressUUID,
        })}
      />
    );
  }

  return (
    <Layout
      hideNav
      isLoading={loading}
      renderHeader={() => <SellerIndependentPageHeader title="Show us your home" />}
      hideFooter
    >
      <SSVATasksContent
        remainingTasks={remainingTasks}
        completedTasks={completedTasks}
        openParticipantsModal={openParticipantsModal}
        hasUpcomingOR={orvaState === ORVAState.ORVA_SCHEDULED}
        shouldSeeAgentExperience={shouldSeeAgentExperience}
        shouldSeeSsvaNooExperience={shouldSeeSsvaNooExperience}
      />
    </Layout>
  );
};

interface SSVATasksContentProps {
  remainingTasks: TasksSectionContentProps['tasks'] | undefined;
  completedTasks: TasksSectionContentProps['tasks'] | undefined;
  /** If true, hides the switch to ORVA card */
  shouldSeeAgentExperience: boolean;
  /** Function to call to open the participants modal */
  openParticipantsModal: () => void;
  /** If true, hides the switch to ORVA card */
  hasUpcomingOR: boolean;
  /** If the customer is in the SSVA NOO experiment */
  shouldSeeSsvaNooExperience: boolean;
}

/**
 * Content to display in the SSVA page for customers in treatment
 * for the SSVA Targeting experiment
 */
const SSVATasksContent: React.FC<SSVATasksContentProps> = ({
  remainingTasks,
  completedTasks,
  openParticipantsModal,
  hasUpcomingOR,
  shouldSeeAgentExperience,
  shouldSeeSsvaNooExperience,
}) => (
  <VALayout
    vaExperience="ssva"
    hideSwitchCard={hasUpcomingOR || shouldSeeAgentExperience || !shouldSeeSsvaNooExperience}
  >
    <Box>
      <Card
        variant="tertiary"
        py={8}
        px={[7, 7, 8]}
        gridGap={8}
        borderRadius={['none', 'none', 'semiRounded']}
        borderColor={['transparent', 'transparent', 'neutrals20']}
      >
        {remainingTasks && (
          <>
            <Flex flexDir="column" gridGap={[4, 4, 5]}>
              <SectionTitle description="An Opendoor Expert will review your info once you complete all your tasks.">
                Your Task List
              </SectionTitle>

              <TasksSectionContent tasks={remainingTasks} testId="remaining-tasks" />
            </Flex>

            <Divider />
          </>
        )}

        {completedTasks && (
          <Flex flexDir="column" gridGap={5}>
            <SectionTitle>Completed Tasks</SectionTitle>

            {completedTasks?.length > 0 ? (
              <TasksSectionContent tasks={completedTasks} testId="completed-tasks" />
            ) : (
              <Box bg="neutrals20" borderRadius="semiRounded" px={7} py={[6, null, 7]}>
                <Text color="neutrals90" fontWeight="semibold">
                  Completed tasks will show up here
                </Text>
              </Box>
            )}
          </Flex>
        )}
      </Card>

      <Flex flexDir="column" mt={9} px={[7, 7, 0]} gridGap="9">
        <List
          variant="text"
          flexDir={['column', null, null, 'row']}
          gridGap={[5, 5, 5, 7]}
          display="flex"
        >
          <ListItem flex="1">
            <EventCard
              analyticsName="ssva-share-link-with-participant"
              onClick={openParticipantsModal}
              title="Need another person to step in?"
              subtitle="Invite others to your dashboard"
              chipOrIconProps={{ name: 'invite-filled', color: 'neutrals70' }}
              backgroundColor="neutrals30"
              textAsColumn
            />
          </ListItem>
          <ListItem data-testid="ssva-text-reminder-trigger" flex="1">
            <TextReminderTrigger />
          </ListItem>
        </List>

        <Divider />

        <SSVACommonQuestions mt={[0, 0, 5]} />
      </Flex>
    </Box>
  </VALayout>
);

/** Title for the SSVA page */
const SectionTitle: React.FC<{ description?: string }> = ({ description, children }) => (
  <Box gridGap={[3, 3, 4]}>
    <Text as="h2" fontWeight="bold" fontSize={['s3', null, 's4']}>
      {children}
    </Text>
    <Text fontSize={['s0', null, 's1']} lineHeight={['s0', null, 's1']}>
      {description}
    </Text>
  </Box>
);
